import React, { useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import NativeReactionPicker from './NativeReactionPicker';
import IconButton from '@/components/Buttons/IconButton';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { useChatStore } from '@/store';
import { fetchWithAuth } from '@/lib/api/client';

const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👋', '🎉', '💡', '👎'];
export default function MessageReactions({ conversationId, message, isOwner, buttonSize = 28, iconSize = 13, buttonStyle }) {
  const { colors } = useTheme();
  const isWorkChat = useChatStore(state => !!state.chats.find(chat => chat.id === conversationId)?.coworkerSession);
  const pending = useRef(false);
  const native = Platform.OS === 'ios' || Platform.OS === 'android';
  const targetSize = native ? Math.max(44, buttonSize) : buttonSize;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isUser = message.role === 'user';
  const selected = message.reactions?.[isUser ? 'agent' : 'user'];
  if (!isWorkChat || !message.id || message.isStreaming || message.isWaiting || message.flags?.isDeleted || message.flags?.isHidden) return null;
  if (isUser && !selected) return null;
  const choose = async emoji => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await fetchWithAuth(`/json/conversations/${conversationId}/messages/${message.id}/reaction`, { method: 'PUT', body: { emoji: selected === emoji ? null : emoji } });
      useChatStore.getState().updateLocalChatMessage(conversationId, message.id, { reactions: result.reactions });
      setOpen(false);
    } catch (_) { setError('Couldn’t save your reaction. Try again.'); }
    finally { pending.current = false; setBusy(false); }
  };
  const chip = { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 18, borderWidth: 2, borderColor: colors.solid_color_primary, minWidth: 36, alignItems: 'center', backgroundColor: colors.solid_color_secondary };
  return <View style={isUser
    ? { alignSelf: 'flex-end', marginRight: 10, marginTop: -8, marginBottom: 4, zIndex: 1 }
    : { flexDirection: 'row', alignItems: 'center', position: 'relative' }}>
    {!!selected && <Pressable disabled={isUser || !isOwner || busy} onPress={() => choose(selected)} accessibilityRole="button" accessibilityLabel={isUser ? `Bot reacted ${selected}` : `Remove your ${selected} reaction`} style={isUser ? chip : { width: targetSize, height: targetSize, alignItems: 'center', justifyContent: 'center' }}><Text>{selected}</Text></Pressable>}
    {!isUser && isOwner && native && <>
      <IconButton icon="face-smile" accessibilityLabel="React to reply" title="React to reply" onPress={() => { setError(''); setOpen(true); }} height={targetSize} width={targetSize} iconSize={18} color={colors.text_color_secondary} disabled={busy} />
      <NativeReactionPicker visible={open} onClose={() => setOpen(false)} emojis={EMOJIS} selected={selected} onSelect={choose} busy={busy} error={error} />
    </>}
    {!isUser && isOwner && !native && <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild={Platform.OS === 'web'}>
        <IconButton icon="face-smile" title="React to reply" accessibilityLabel="React to reply"
          tooltip={open ? undefined : 'React to reply'} iconSize={iconSize} height={buttonSize} width={buttonSize}
          color={colors.text_color_secondary} style={StyleSheet.flatten(buttonStyle)} disabled={busy} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="DropdownMenuContent MessageReactionPicker" side="top" align="start" sideOffset={8} collisionPadding={12} loop>
        {EMOJIS.map(emoji => <DropdownMenu.Item key={emoji} className="MessageReactionOption"
          data-selected={selected === emoji ? 'true' : undefined}
          disabled={busy} onSelect={() => choose(emoji)} aria-label={`React ${emoji}`}>
          <DropdownMenu.ItemTitle>{emoji}</DropdownMenu.ItemTitle>
        </DropdownMenu.Item>)}
      </DropdownMenu.Content>
    </DropdownMenu.Root>}
    {!!error && (!native || !open) && <View style={{ position: 'absolute', top: buttonSize + 4, left: 0, width: 240, padding: 8, borderRadius: 8, backgroundColor: colors.solid_color_secondary, zIndex: 1 }}>
      <Text size="xs" color="danger" accessibilityRole="alert">{error}</Text>
    </View>}
  </View>;
}
